import { deviceApi } from "#/api/deviceApi";
import { itemsApi } from "#/api/itemsApi";
import { productApi } from "#/api/productApi";
import { rfidTagApi } from "#/api/rfidTagApi";
import { sitesApi } from "#/api/sitesApi";
import { Skeleton, Title } from "@mantine/core";
import { IconCategory, IconDeviceMobile, IconExternalLink, IconMapPin, IconPackage, IconTag } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const AnalyticsCards = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Fetch sites count
  const { data: sitesData, isFetching: isFetchingSites } = useQuery({
    queryKey: ["sites-count"],
    queryFn: async () => {
      const response = await sitesApi.getAll({ page: 1, limit: 1 });
      return { total: response.data.total };
    },
    staleTime: 1000 * 60,
  });

  // Fetch items count
  const { data: itemsData, isFetching: isFetchingItems } = useQuery({
    queryKey: ["items-count"],
    queryFn: async () => {
      const response = await itemsApi.getAll({ page: 1, limit: 1 });
      return { total: response.data.total };
    },
    staleTime: 1000 * 60,
  });

  // Fetch categories count
  const { data: categoriesData, isFetching: isFetchingCategories } = useQuery({
    queryKey: ["categories-count"],
    queryFn: async () => {
      const response = await productApi.getAll({ page: 1, limit: 1 });
      return { total: response.data.total };
    },
    staleTime: 1000 * 60,
  });

  // Fetch RFIDs count
  const { data: rfidsData, isFetching: isFetchingRfids } = useQuery({
    queryKey: ["rfids-count"],
    queryFn: async () => {
      const response = await rfidTagApi.getAll({ page: 1, limit: 1 });
      return { total: response.data.total };
    },
    staleTime: 1000 * 60,
  });

  // Fetch Devices count
  const { data: devicesData, isFetching: isFetchingDevices } = useQuery({
    queryKey: ["devices-count"],
    queryFn: async () => {
      const response = await deviceApi.getAll({ page: 1, limit: 1 });
      return { total: response.data.total };
    },
    staleTime: 1000 * 60,
  });

  const cards = [
    {
      title: t("dashboard.sites"),
      count: sitesData?.total ?? 0,
      icon: IconMapPin,
      color: "blue",
      loading: isFetchingSites,
      link: "/sites",
    },
    {
      title: t("dashboard.items"),
      count: itemsData?.total ?? 0,
      icon: IconPackage,
      color: "brown",
      loading: isFetchingItems,
      link: "/inventory/items",
      customColors: {
        borderColor: "#A0826D",
        backgroundColor: "#F5E6D3",
        iconBg: "#E8D4BF",
        textColor: "#6B4423",
      }
    },
    {
      title: t("dashboard.categories"),
      count: categoriesData?.total ?? 0,
      icon: IconCategory,
      color: "orange",
      loading: isFetchingCategories,
      link: "/inventory/categories",
    },
    {
      title: t("dashboard.rfidScanners"),
      count: devicesData?.total ?? 0,
      icon: IconDeviceMobile,
      color: "grape",
      loading: isFetchingDevices,
      link: "/devices",
    },
    {
      title: t("dashboard.rfidTags"),
      count: rfidsData?.total ?? 0,
      icon: IconTag,
      color: "pink",
      loading: isFetchingRfids,
      link: "/rfids",
    },
  ];

  return (
    <div className="w-full flex flex-col gap-2">
      <Title order={4}>{t("dashboard.overview")}</Title>
      <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className="border rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow"
              style={{
                borderColor: card.customColors?.borderColor || `var(--mantine-color-${card.color}-3)`,
                backgroundColor: card.customColors?.backgroundColor || `var(--mantine-color-${card.color}-0)`,
              }}
              onClick={() => navigate(card.link)}
            >
              {card.loading ? (
                <Skeleton height={80} />
              ) : (
                <>
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="text-3xl font-extrabold"
                      style={{ color: card.customColors?.textColor || `var(--mantine-color-${card.color}-7)` }}
                    >
                      {card.count.toLocaleString()}
                    </div>
                    <IconExternalLink size={16} className="opacity-60" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="p-1.5 rounded-lg"
                      style={{
                        backgroundColor: card.customColors?.iconBg || `var(--mantine-color-${card.color}-1)`,
                      }}
                    >
                      <Icon
                        size={18}
                        style={{ color: card.customColors?.textColor || `var(--mantine-color-${card.color}-7)` }}
                      />
                    </div>
                    <div className="text-sm font-bold text-black">
                      {card.title}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AnalyticsCards;
